import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  ArrowUpRight, Loader2, Plus, CalendarRange,
  KeyRound, Undo2, CircleCheck, Wrench,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const BADGE = {
  Confirmada: "bg-green-100 text-green-800",
  Pendiente: "bg-amber-100 text-amber-800",
  Activa: "bg-blue-100 text-blue-800",
  Completada: "bg-gray-200 text-gray-700",
  Cancelada: "bg-red-100 text-red-700",
};

const FLEET_COLORS = {
  Disponible: "#FFC107",
  Reservado: "#2B2F36",
  Alquilado: "#1A1D23",
  Mantenimiento: "#C7CBD1",
  "Fuera de servicio": "#EF4444",
};

function Card({ children, className = "", ...props }) {
  return (
    <div
      className={`bg-white rounded-lg border border-gray-200 shadow-sm ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

function KpiCard({ kpi, i }) {
  const data = kpi.trend.map((v, idx) => ({ x: idx, v }));
  return (
    <Card
      className="p-4 animate-fade-up"
      style={{ animationDelay: `${i * 70}ms` }}
      data-testid={`kpi-card-${i}`}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-gray/60">
        {kpi.label}
      </p>
      <div className="flex items-end justify-between mt-1.5">
        <span className="text-2xl font-extrabold tracking-tight text-brand-graphite">
          {kpi.value}
        </span>
        <span className="flex items-center gap-0.5 text-xs font-bold text-green-600 mb-0.5">
          <ArrowUpRight className="h-3.5 w-3.5" />
          {kpi.delta}
        </span>
      </div>
      <div className="h-7 mt-2 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          {kpi.type === "line" ? (
            <LineChart data={data}>
              <Line type="monotone" dataKey="v" stroke="#FFC107" strokeWidth={2.5} dot={false} />
            </LineChart>
          ) : (
            <BarChart data={data}>
              <Bar dataKey="v" fill="#FFC107" radius={[2, 2, 0, 0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function StatTile({ icon: Icon, label, value, accent }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-100 bg-brand-light/40 px-4 py-3">
      <span className={`flex h-10 w-10 items-center justify-center rounded-md ${accent}`}>
        <Icon className="h-5 w-5" strokeWidth={2} />
      </span>
      <div className="leading-tight">
        <p className="text-2xl font-extrabold tracking-tight text-brand-graphite">{value}</p>
        <p className="text-xs font-medium text-brand-gray/60">{label}</p>
      </div>
    </div>
  );
}

function AddIncomeDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!amount || Number(amount) <= 0) return toast.error("Ingresa un monto válido");
    setSaving(true);
    try {
      await api.post("/incomes", { date, amount: Number(amount), description });
      toast.success("Ingreso registrado");
      setOpen(false);
      setAmount("");
      setDescription("");
      onDone();
    } catch {
      toast.error("No se pudo guardar el ingreso");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="add-income-button"
          className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md bg-brand-yellow text-brand-graphite hover:bg-[#e6ac00] transition-colors"
        >
          <Plus className="h-3.5 w-3.5" /> Registrar ingreso
        </button>
      </DialogTrigger>
      <DialogContent data-testid="income-dialog">
        <DialogHeader><DialogTitle>Registrar ingreso</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Fecha</Label>
            <Input type="date" value={date} data-testid="income-date"
              onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Monto (USD)</Label>
            <Input type="number" min="0" step="0.01" value={amount} placeholder="0.00"
              data-testid="income-amount" onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Descripción</Label>
            <Input value={description} placeholder="Alquiler, seguro, cargo adicional…"
              data-testid="income-description" onChange={(e) => setDescription(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="income-save"
            className="bg-brand-yellow text-brand-graphite hover:bg-[#e6ac00] font-bold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/dashboard/summary").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-6 w-6 animate-spin text-brand-graphite" />
      </div>
    );
  }

  const fleetVal = (n) => data.fleet_status.find((f) => f.name === n)?.value ?? 0;

  return (
    <div className="space-y-5" data-testid="dashboard">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-brand-graphite">
          Buenos días, {user?.name}
        </h1>
        <p className="text-brand-gray/70 mt-0.5 text-sm">
          Aquí tienes el resumen de tu negocio de alquiler hoy.
        </p>
      </div>

      {/* FILA 1 — KPIs */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        {data.kpis.map((k, i) => (
          <KpiCard key={k.label} kpi={k} i={i} />
        ))}
      </div>

      {/* FILA 2 y 3 — grilla unificada; en móvil se prioriza Actividad y Reservas */}
      <div className="grid grid-cols-1 lg:grid-cols-10 gap-4">
        <Card className="p-5 order-3 lg:order-none lg:col-span-7" data-testid="daily-revenue-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CalendarRange className="h-4 w-4 text-brand-graphite" />
              <h3 className="font-bold text-brand-graphite">Ingresos Diarios</h3>
              <span className="text-xs font-semibold text-brand-gray/50">Últimos 30 días</span>
            </div>
            <AddIncomeDialog onDone={load} />
          </div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.daily_revenue} margin={{ left: -12, right: 8, top: 8 }}>
                <CartesianGrid vertical={false} stroke="#F3F4F6" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} interval={4}
                  tick={{ fontSize: 11, fill: "#9ca3af" }} />
                <YAxis tickLine={false} axisLine={false}
                  tick={{ fontSize: 11, fill: "#9ca3af" }}
                  tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                <Tooltip
                  formatter={(v) => [`$${Number(v).toLocaleString()}`, "Ingresos"]}
                  contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13 }} />
                <Bar dataKey="revenue" fill="#FFC107" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 order-4 lg:order-none lg:col-span-3" data-testid="fleet-card">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-brand-graphite">Estado de la Flota</h3>
            <span className="text-xs font-bold text-brand-graphite">
              {data.fleet_utilization}% <span className="font-medium text-brand-gray/50">uso</span>
            </span>
          </div>
          <div className="h-36">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.fleet_status} dataKey="value" nameKey="name"
                  innerRadius={42} outerRadius={62} paddingAngle={2} stroke="none">
                  {data.fleet_status.map((e) => (
                    <Cell key={e.name} fill={FLEET_COLORS[e.name]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-2 space-y-1.5">
            {data.fleet_status.map((e) => (
              <li key={e.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-brand-gray">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: FLEET_COLORS[e.name] }} />
                  {e.name}
                </span>
                <span className="font-bold text-brand-graphite">{e.value}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="overflow-hidden order-2 lg:order-none lg:col-span-7" data-testid="reservations-card">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-bold text-brand-graphite">Reservas Recientes</h3>
            <Link
              to="/reservations"
              data-testid="ver-todas-reservas"
              className="text-xs font-bold text-brand-graphite hover:text-[#b98900] transition-colors"
            >
              Ver todas →
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-brand-gray/50 bg-gray-50">
                  <th className="px-5 py-2.5 font-semibold">Cliente</th>
                  <th className="px-5 py-2.5 font-semibold">Vehículo</th>
                  <th className="px-5 py-2.5 font-semibold">Recogida</th>
                  <th className="px-5 py-2.5 font-semibold">Devolución</th>
                  <th className="px-5 py-2.5 font-semibold">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.recent_reservations.slice(0, 5).map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50/60 transition-colors" data-testid={`reservation-row-${i}`}>
                    <td className="px-5 py-3 font-semibold text-brand-graphite">{r.customer}</td>
                    <td className="px-5 py-3 text-brand-gray">{r.vehicle}</td>
                    <td className="px-5 py-3 text-brand-gray">{r.pickup}</td>
                    <td className="px-5 py-3 text-brand-gray">{r["return"]}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${BADGE[r.status]}`}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-5 order-1 lg:order-none lg:col-span-3" data-testid="today-activity-card">
          <h3 className="font-bold text-brand-graphite mb-4">Actividad de Hoy</h3>
          <div className="grid grid-cols-2 gap-3">
            <StatTile icon={KeyRound} label="Recogidas de hoy"
              value={data.todays_pickups.length} accent="bg-brand-yellow text-brand-graphite" />
            <StatTile icon={Undo2} label="Devoluciones de hoy"
              value={data.todays_returns.length} accent="bg-brand-graphite text-brand-yellow" />
            <StatTile icon={CircleCheck} label="Vehículos disponibles"
              value={fleetVal("Disponible")} accent="bg-green-100 text-green-700" />
            <StatTile icon={Wrench} label="En mantenimiento"
              value={fleetVal("Mantenimiento")} accent="bg-gray-200 text-brand-gray" />
          </div>
        </Card>
      </div>
    </div>
  );
}
